<template>
  <ul
    class="scrollbar-h-px extend-width mb-6 flex w-full overflow-y-hidden overflow-x-scroll desktop:flex-wrap desktop:overflow-x-hidden"
  >
    <li
      v-for="(item, index) in contextTitles"
      :key="item"
      class="mr-2 desktop:mb-2"
    >
      <button
        class="rounded-3xl border border-solid border-gray-400 px-4 py-2 font-bold text-blue-400 hover:bg-blue-400/10 hover:text-blue-400"
        :class="current === index && 'bg-blue-400/10'"
        :title="item"
        @click="$emit('select', index)"
      >
        <span class="truncate">
          {{ item }}
        </span>
      </button>
    </li>
  </ul>
  <div class="flex overflow-hidden rounded shadow-normal shadow-gray-400">
    <div
      class="relative h-80 w-1/2 overflow-hidden bg-cover bg-center bg-no-repeat p-5 before:absolute before:left-0 before:top-0 before:h-full before:w-full before:bg-gradient-to-b before:from-black/60 before:from-10% before:to-transparent before:content-['']"
      :style="provideBackgroundUrl(contextCard.bgImg)"
    >
      <div class="relative text-white">
        <h3 class="relative mb-2 text-lg font-bold">
          {{ contextCard.innerTitle }}
        </h3>
        <p v-if="isEU && contextCard.description" class="mb-3 text-sm">
          {{ contextCard.description }}
        </p>
        <ul v-if="contextCard.traits.length" class="list-disc pl-6">
          <li v-for="trait in contextCard.traits" :key="trait" class="text-sm">
            {{ trait }}
          </li>
        </ul>
      </div>
    </div>
    <div class="flex h-80 w-1/2 flex-col justify-between p-5">
      <div class="container-scrollbar flex-1 overflow-y-auto overflow-x-hidden">
        <template v-if="contextCard.solutionApps.length">
          <h4 class="mb-2 text-base font-bold">
            {{ contextCard.productSectionTitle }}
          </h4>
          <ul class="flex flex-wrap items-center">
            <li
              v-for="app in contextCard.solutionApps"
              :key="app.productName"
              class="mb-2 w-1/3"
            >
              <TheNuxtLink
                v-if="app.productUrl"
                :to="app.productUrl"
                :title="app.productName"
                class="block hover:text-blue-500 hover:underline"
              >
                <figure
                  class="mx-auto mb-1 h-10 w-10 overflow-hidden rounded-lg"
                >
                  <img
                    :src="provideImgUrl(app.productIcon)"
                    alt=""
                    loading="lazy"
                    class="pointer-events-none h-full w-full object-cover"
                  />
                </figure>
                <span class="block text-center text-xs font-bold">
                  {{ app.productName }}
                </span>
              </TheNuxtLink>
            </li>
          </ul>
        </template>

        <template v-if="contextCard.successStories?.length && isCN">
          <h4 class="mb-2 mt-3 text-base font-bold">
            {{ contextCard.successStoryTitle }}
          </h4>
          <ul class="flex flex-wrap items-center">
            <li
              v-for="successItem in contextCard.successStories"
              :key="successItem.name"
              class="mb-2 w-1/3"
            >
              <TheNuxtLink
                :to="successItem.url"
                :title="successItem.name"
                class="block overflow-hidden rounded bg-white"
              >
                <figure
                  v-if="successItem.icon"
                  class="relative aspect-video w-full overflow-hidden"
                >
                  <img
                    :src="provideImgUrl(successItem.icon)"
                    :alt="successItem.name"
                    loading="lazy"
                    class="pointer-events-none absolute left-0 top-0 h-full w-full object-contain"
                  />
                </figure>
              </TheNuxtLink>
            </li>
          </ul>
        </template>
      </div>
      <div class="flex w-full items-center justify-between pt-2">
        <TheButton
          v-if="contextCard.btnOneUrl"
          :title="contextCard.btnOne"
          :to="contextCard.btnOneUrl"
          class="mr-5 w-full"
        >
          <span class="text-sm">{{ contextCard.btnOne }}</span>
        </TheButton>
        <TheButton
          :id="contextAcnTrackingID"
          :title="contextCard.btnTwo"
          :to="contextDomainSupportURL"
          type="outline"
          class="w-full"
        >
          <span class="text-sm">{{ contextCard.btnTwo }}</span>
        </TheButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { ISolutionCard } from '@/assets/types/Home';

  interface IProps {
    cards: ISolutionCard[];
    current: number;
  }

  const props = withDefaults(defineProps<IProps>(), {
    cards: () => [],
  });

  defineEmits<{
    (e: 'select', index: number): void;
  }>();

  const { locale } = useI18n();
  const { provideImgUrl, provideBackgroundUrl } = useHomeData();
  const { isEU, isCN } = useLanguage();

  const contextDomainSupportURL = computed((): string => {
    return `/${locale.value}/marketplace/help/sales-support?IssueType=1`;
  });

  const contextAcnTrackingID = computed((): string | undefined => {
    if (isCN.value) {
      return 'tracking_ssupport';
    }
    return undefined;
  });

  const contextTitles = computed((): string[] =>
    props.cards.map((card: ISolutionCard) => card.outerTitle)
  );

  const contextCard = computed((): ISolutionCard => {
    return props.cards[props.current];
  });
</script>
